import React from 'react';
import { makeStyles, tokens, mergeClasses, Tooltip } from '@fluentui/react-components';
import {
  ChevronLeftRegular,
  ChevronRightRegular,
  ChevronDownRegular,
} from '@fluentui/react-icons';

// ── Types ──────────────────────────────────────────────────────────────────

type CollapseDirection = 'vertical' | 'horizontal';

interface CollapsiblePanelProps {
  /** Panel title shown in the header / collapsed strip */
  title: string;
  /** Optional icon rendered before the title */
  icon?: React.ReactElement;
  /** Whether the panel is currently collapsed */
  isCollapsed: boolean;
  /** Toggle callback */
  onToggle: () => void;
  /** Prevents the user from collapsing this panel (guard rule) */
  disabled?: boolean;
  /**
   * Collapse direction:
   * - `vertical` — collapses to a thin vertical strip (side panels)
   * - `horizontal` — collapses to a thin horizontal strip (bottom panels)
   * @default 'vertical'
   */
  direction?: CollapseDirection;
  /**
   * Which side a vertical-collapsed strip should stick to.
   * - `left` — border on right side (default, for left-side panels)
   * - `right` — border on left side (for right-side panels that collapse leftward)
   * @default 'left'
   */
  collapseSide?: 'left' | 'right';
  children: React.ReactNode;
}

// ── Collapsed strip width / height ─────────────────────────────────────────

const COLLAPSED_STRIP_SIZE = '28px';

// ── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  /* ── Expanded state ─────────────────────────────────────────────────── */

  expanded: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
    minHeight: 0,
    flex: 1,
    height: '100%',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0 12px',
    height: COLLAPSED_STRIP_SIZE,
    minHeight: COLLAPSED_STRIP_SIZE,
    maxHeight: COLLAPSED_STRIP_SIZE,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    userSelect: 'none',
    flexShrink: 0,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },

  headerTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  headerIcon: {
    display: 'flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '16px',
    flexShrink: 0,
  },

  chevron: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    color: tokens.colorNeutralForeground3,
    fontSize: '16px',
    flexShrink: 0,
  },

  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  },

  /* ── Collapsed vertical strip (side panels) ────────────────────────── */

  collapsedVerticalBase: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: COLLAPSED_STRIP_SIZE,
    minWidth: COLLAPSED_STRIP_SIZE,
    maxWidth: COLLAPSED_STRIP_SIZE,
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'pointer',
    userSelect: 'none',
    overflow: 'hidden',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },

  collapsedVerticalBorderRight: {
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  collapsedVerticalBorderLeft: {
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  collapsedVerticalInner: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    whiteSpace: 'nowrap',
  },

  collapsedVerticalTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },

  collapsedVerticalIcon: {
    display: 'flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
  },

  /* ── Collapsed horizontal strip (bottom panels) ────────────────────── */

  collapsedHorizontal: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    height: COLLAPSED_STRIP_SIZE,
    minHeight: COLLAPSED_STRIP_SIZE,
    maxHeight: COLLAPSED_STRIP_SIZE,
    padding: '0 12px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    userSelect: 'none',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },

  collapsedHorizontalTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },

  collapsedHorizontalIcon: {
    display: 'flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
  },

  /* ── Disabled overlay ──────────────────────────────────────────────── */

  disabledHeader: {
    cursor: 'not-allowed',
    opacity: 0.5,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
});

// ── Component ──────────────────────────────────────────────────────────────

const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
  title,
  icon,
  isCollapsed,
  onToggle,
  disabled = false,
  direction = 'vertical',
  collapseSide = 'left',
  children,
}) => {
  const styles = useStyles();

  const handleClick = () => {
    if (disabled) return;
    onToggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  /** Shared a11y props for all interactive toggle surfaces */
  const a11yProps = {
    role: 'button' as const,
    tabIndex: disabled ? -1 : 0,
    'aria-expanded': !isCollapsed,
    'aria-label': isCollapsed ? `Expand ${title}` : `Collapse ${title}`,
    onKeyDown: handleKeyDown,
  };

  // ── Collapsed: vertical strip ──────────────────────────────────────
  if (isCollapsed && direction === 'vertical') {
    const stripClass = mergeClasses(
      styles.collapsedVerticalBase,
      collapseSide === 'right' ? styles.collapsedVerticalBorderLeft : styles.collapsedVerticalBorderRight,
    );
    const strip = (
      <div className={stripClass} onClick={handleClick} {...a11yProps}>
        <div className={styles.collapsedVerticalInner}>
          {icon && <span className={styles.collapsedVerticalIcon}>{icon}</span>}
          <span className={styles.collapsedVerticalTitle}>{title}</span>
        </div>
      </div>
    );

    return disabled ? strip : (
      <Tooltip content={`Expand ${title}`} relationship="label">
        {strip}
      </Tooltip>
    );
  }

  // ── Collapsed: horizontal strip ────────────────────────────────────
  if (isCollapsed && direction === 'horizontal') {
    const strip = (
      <div className={styles.collapsedHorizontal} onClick={handleClick} {...a11yProps}>
        <ChevronRightRegular />
        {icon && <span className={styles.collapsedHorizontalIcon}>{icon}</span>}
        <span className={styles.collapsedHorizontalTitle}>{title}</span>
      </div>
    );

    return disabled ? strip : (
      <Tooltip content={`Expand ${title}`} relationship="label">
        {strip}
      </Tooltip>
    );
  }

  // ── Expanded ───────────────────────────────────────────────────────
  return (
    <div className={styles.expanded}>
      <div
        className={mergeClasses(styles.header, disabled && styles.disabledHeader)}
        onClick={handleClick}
        {...a11yProps}
      >
        {icon && <span className={styles.headerIcon}>{icon}</span>}
        <span className={styles.headerTitle}>{title}</span>
        <span className={styles.chevron}>
          {direction === 'horizontal' ? (
            <ChevronDownRegular />
          ) : (
            <ChevronLeftRegular />
          )}
        </span>
      </div>
      <div className={styles.body}>{children}</div>
    </div>
  );
};

export default CollapsiblePanel;